/**
 * Per-platform media attachment limits and mixing rules.
 *
 * Sources (researched Apr 2026):
 * - Facebook: https://www.facebook.com/help/publisher (up to ~30 photos per
 *   post, 1 video per post; photo + video albums allowed. Feed UI collapses
 *   to a 2x2 grid with "+N more" overlay after 4).
 * - Instagram: carousel posts support up to 10 items (images OR videos or
 *   mixed); single post is 1 image or 1 video; Reels = 1 video. Square 1:1
 *   crop in feed, swipeable dot indicator.
 * - X (Twitter): 1 tweet can attach up to 4 images OR 1 GIF OR 1 video.
 *   Cannot mix images and video in the same tweet.
 * - LinkedIn: up to 9 images per post OR 1 video OR 1 document. Cannot mix
 *   images and video. Feed UI uses 2x2 grid with "+N more".
 * - TikTok: 1 video per post (up to 10 min) OR a photo carousel of up to
 *   35 images. Cannot mix in a single post.
 * - YouTube: the main post is 1 video (uploads). Community posts support
 *   1 image or a poll.
 * - Blog: unlimited; images/videos embedded inline via the editor.
 *
 * Shape:
 *   images        — max number of images per post
 *   videos        — max number of videos per post
 *   total         — hard cap across image + video (e.g. Instagram carousel = 10)
 *   allowMix      — can images and videos live in the same post?
 *   note          — short human-readable summary (used in toast warnings)
 */

export const MEDIA_LIMITS = {
    facebook: {
        images: 30,
        videos: 1,
        total: 30,
        allowMix: true,
        note: 'Facebook: up to 30 photos or 1 video per post.',
    },
    instagram: {
        images: 10,
        videos: 10,
        total: 10,
        allowMix: true,
        note: 'Instagram carousel: up to 10 images/videos per post.',
    },
    x: {
        images: 4,
        videos: 1,
        total: 4,
        allowMix: false,
        note: 'X (Twitter): up to 4 images OR 1 video per tweet. No mixing.',
    },
    twitter: {
        images: 4,
        videos: 1,
        total: 4,
        allowMix: false,
        note: 'X (Twitter): up to 4 images OR 1 video per tweet. No mixing.',
    },
    linkedin: {
        images: 9,
        videos: 1,
        total: 9,
        allowMix: false,
        note: 'LinkedIn: up to 9 images OR 1 video per post. No mixing.',
    },
    tiktok: {
        images: 35,
        videos: 1,
        total: 35,
        allowMix: false,
        note: 'TikTok: 1 video OR a photo carousel up to 35 images. No mixing.',
    },
    youtube: {
        images: 1,
        videos: 1,
        total: 1,
        allowMix: false,
        note: 'YouTube community posts: 1 image or 1 video.',
    },
    pinterest: {
        images: 1,
        videos: 1,
        total: 1,
        allowMix: false,
        note: 'Pinterest: 1 image (or 1 video) per Pin. A Pin requires an image.',
    },
    reddit: {
        images: 1,
        videos: 0,
        total: 1,
        allowMix: false,
        note: 'Reddit: 1 image per post (published as a direct image link when post type is Image). Video posts are not supported yet; text and link posts take no media.',
    },
    blog: {
        images: 999,
        videos: 999,
        total: 999,
        allowMix: true,
        note: 'Blog posts: unlimited media embedded inline.',
    },
    website: {
        images: 999,
        videos: 999,
        total: 999,
        allowMix: true,
        note: 'Website posts: unlimited media embedded inline.',
    },
};

/**
 * Validate a proposed media list for a given platform. Returns
 * { ok: boolean, error?: string, trimmed?: media[] }.
 * trimmed is the media array clipped to the platform caps (safe to use
 * directly for rendering previews).
 */
export function validateMediaForPlatform(platform, media) {
    const limits = MEDIA_LIMITS[platform] || MEDIA_LIMITS.facebook;
    if (!media || media.length === 0) return { ok: true, trimmed: [] };

    // Shape-tolerant: server-attached media has type:"image" / "video" (short)
    // while file-uploads have full MIME like "image/jpeg" / "video/mp4". Accept
    // both so platform-limit checks don't silently misclassify server-side rows.
    const isImg = (m) => {
        const t = String(m && m.type || '').toLowerCase();
        return t.startsWith('image/') || t === 'image';
    };
    const isVid = (m) => {
        const t = String(m && m.type || '').toLowerCase();
        return t.startsWith('video/') || t === 'video';
    };
    const images = media.filter(isImg);
    const videos = media.filter(isVid);

    if (!limits.allowMix && images.length > 0 && videos.length > 0) {
        return {
            ok: false,
            error: `${platform} does not allow mixing images and videos in one post.`,
            limits,
        };
    }
    if (images.length > limits.images) {
        return {
            ok: false,
            error: `${platform} allows at most ${limits.images} image${limits.images === 1 ? '' : 's'} per post.`,
            limits,
            trimmed: [...videos, ...images.slice(0, limits.images)],
        };
    }
    if (videos.length > limits.videos) {
        return {
            ok: false,
            error: `${platform} allows at most ${limits.videos} video${limits.videos === 1 ? '' : 's'} per post.`,
            limits,
            trimmed: [...images, ...videos.slice(0, limits.videos)],
        };
    }
    if (media.length > limits.total) {
        return {
            ok: false,
            error: `${platform} allows at most ${limits.total} media items per post.`,
            limits,
            trimmed: media.slice(0, limits.total),
        };
    }
    return { ok: true, trimmed: media, limits };
}

/**
 * Clamp a media list to the platform's limits for preview rendering.
 * Never throws; always returns something safe to render.
 */
export function clampMediaForPlatform(platform, media) {
    const result = validateMediaForPlatform(platform, media);
    return result.trimmed || media || [];
}

/**
 * Recommended AI image generation size per platform.
 * Maps to DALL-E 3 / Imagen / Flux supported sizes.
 */
export const PLATFORM_IMAGE_SIZES = {
    instagram: '1024x1024',   // 1:1 square (feed default)
    facebook:  '1792x1024',   // landscape
    x:         '1024x1024',   // square
    linkedin:  '1792x1024',   // landscape
    tiktok:    '1024x1792',   // 9:16 portrait
    youtube:   '1792x1024',   // 16:9 landscape
    pinterest: '1024x1792',   // 2:3 vertical (Pinterest's preferred Pin ratio)
    reddit:    '1024x1024',   // square — safe default across Reddit clients
    blog:      '1792x1024',   // landscape (featured image)
};
